import { useEffect, useRef, useState, type ReactNode } from "react";
import { useAuth } from "../auth/AuthContext";
import { AppFooter } from "../components/AppFooter";
import { BrandLogo, IconCalendar, IconChart, IconChat, IconHelp, IconScale, IconHome, IconMore, IconPulse, IconUser } from "../components/icons";
import { NotificationBell } from "../components/NotificationBell";
import { ThemeToggle } from "../components/ThemeToggle";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { Link, useLocation } from "../router/router";
import { t } from "../i18n";

/** Seções que ficam fixas na barra inferior do celular; as demais vão para o menu "Mais" (no máximo 5 itens na barra). */
const FIXAS_NO_CELULAR = 4;

/** Navegação simplificada: barra superior + abas (rodapé no celular: 4 seções + "Mais"). */
export function AthleteLayout({ children }: { children: ReactNode }) {
  const { me, membership, logout } = useAuth();
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreBtn = useRef<HTMLButtonElement>(null);
  const sheet = useRef<HTMLDivElement>(null);

  useEffect(() => setMoreOpen(false), [pathname]);          // trocou de tela: fecha o menu
  useEffect(() => {
    if (!moreOpen) return;
    sheet.current?.querySelector<HTMLElement>("a")?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMoreOpen(false);
        moreBtn.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [moreOpen]);

  if (!me || !membership) return null;
  const base = `/org/${membership.organization_id}/atleta`;
  const tabs = [
    { to: base, label: t("Início"), icon: <IconHome /> },
    { to: `${base}/calendario`, label: t("Calendário"), icon: <IconCalendar /> },
    { to: `${base}/atividades`, label: t("Atividades"), icon: <IconPulse /> },
    { to: `${base}/evolucao`, label: t("Evolução"), icon: <IconChart /> },
    { to: `${base}/corpo`, label: t("Peso"), icon: <IconScale /> },
    { to: `${base}/mensagens`, label: t("Mensagens"), icon: <IconChat /> },
    { to: `${base}/perfil`, label: t("Meu perfil"), icon: <IconUser /> },
    { to: `${base}/ajuda`, label: t("Ajuda"), icon: <IconHelp /> },
  ];
  const managesToo = membership.roles.some((r) => r !== "athlete");
  const isCurrent = (to: string) =>
    pathname === to ||
    (to === `${base}/calendario` && pathname.includes("/atleta/treinos/")) ||
    (to === `${base}/atividades` && pathname.includes("/atleta/atividades/")) ||
    (to === `${base}/ajuda` && pathname.startsWith(`${base}/ajuda/`));
  const extras = tabs.slice(FIXAS_NO_CELULAR);
  const extraAtiva = extras.some((tp) => isCurrent(tp.to));

  return (
    <div className="athlete-shell">
      <a href="#conteudo" className="skip-link">
        {t("Ir para o conteúdo")}
      </a>
      <header className="athlete-top">
        <div className="bar">
          <Link to={base} className="brand" style={{ padding: 0 }}>
            <BrandLogo />
          </Link>
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            {managesToo && (
              <Link to={`/org/${membership.organization_id}`} className="btn btn-ghost btn-sm">
                {t("Área de gestão")}
              </Link>
            )}
            <NotificationBell orgId={membership.organization_id} mode="athlete" />
            <Link to="/conta" className="btn btn-ghost btn-sm">
              {t("Conta")}
            </Link>
            <LanguageSwitcher loggedIn compact />
            <ThemeToggle />
            <button className="btn btn-ghost btn-sm" onClick={logout}>
              {t("Sair")}
            </button>
          </div>
        </div>
      </header>
      <nav className="athlete-tabs" aria-label={t("Seções do atleta")}>
        {tabs.map((tp, i) => (
          <Link
            key={tp.to}
            to={tp.to}
            className={i >= FIXAS_NO_CELULAR ? "tab-extra" : undefined}
            aria-current={isCurrent(tp.to) ? "page" : undefined}
          >
            {tp.icon}
            {tp.label}
          </Link>
        ))}
        <button
          ref={moreBtn}
          type="button"
          className={`tab-more${extraAtiva ? " is-active" : ""}`}
          aria-expanded={moreOpen}
          aria-controls="athlete-more"
          onClick={() => setMoreOpen((v) => !v)}
        >
          <IconMore />
          {t("Mais")}
        </button>
      </nav>
      {moreOpen && (
        <>
          <div className="athlete-more-scrim" onClick={() => setMoreOpen(false)} />
          <div className="athlete-more" id="athlete-more" ref={sheet} role="group" aria-label={t("Mais seções")}>
            {extras.map((tp) => (
              <Link key={tp.to} to={tp.to} aria-current={isCurrent(tp.to) ? "page" : undefined}>
                {tp.icon}
                {tp.label}
              </Link>
            ))}
          </div>
        </>
      )}
      <main className="athlete-main" id="conteudo" tabIndex={-1}>
        {children}
      </main>
      <AppFooter helpTo={`${base}/ajuda`} />
    </div>
  );
}
